// Plant Authentication Handler
// Manages plant button visibility and blurring based on user subscription status.
// Non-premium users (logged-in or not) get only the first 10 vegetables; rest are blurred but visible.

import { subscriptionManager } from './subscription.js';
import { triggerVegetablesFilterWithScrollReset } from './containerFunctions.js';
import { hideAuthModal } from './auth-ui.js';
import { trackCheckoutClick } from './analytics.js';
import { getCheckoutUrl, PRODUCT_ANNUAL } from './checkout-urls.js';

// First 10 vegetables in DOM order (Vegetable section) - only these are accessible for non-premium users
const FIRST_10_VEGETABLES = [
    'Artichokes', 'Arugula', 'Asparagus', 'Beans', 'Beets', 'Bell Pepper',
    'Bitter Gourd', 'Bok Choy', 'Broad Beans', 'Broccoli','Brussel Sprout', 
    'Cabbage', 'Carrots', 'Cauliflower'
];

class PlantAuthManager {
    constructor() {
        this.plantContainer = null;
        this.plantSection = null;
        this.authOverlay = null;
        this.plantButtons = [];
        this.bedSizeControls = null;
        this.lockedPlantModal = null;
        this.subscriptionState = {
            isLoggedIn: false,
            isPremium: false,
            accessiblePlantCount: 10
        };
        
        this.init();
    }

    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupElements());
        } else {
            this.setupElements();
        }
    }

    setupElements() {
        console.log('🔍 Setting up plant auth elements...');
        
        this.plantContainer = document.getElementById('plant-button-container');
        this.plantSection = document.querySelector('.plant-section');
        this.authOverlay = document.getElementById('auth-overlay');
        
        if (!this.plantContainer) {
            console.error('❌ Plant container not found');
            return;
        }
        
        if (!this.plantSection) {
            console.error('❌ Plant section not found');
            return;
        }
        
        if (!this.authOverlay) {
            console.warn('⚠️ Auth overlay not found - plant auth overlay has been removed');
        }

        console.log('✅ Plant container and plant section found');

        // Get all plant buttons (grid-item elements)
        this.plantButtons = Array.from(this.plantContainer.querySelectorAll('.grid-item'));
        console.log(`📊 Found ${this.plantButtons.length} plant buttons`);
        
        // Get bed size controls
        this.bedSizeControls = document.querySelector('.controls');
        if (this.bedSizeControls) {
            console.log('✅ Bed size controls found');
        } else {
            console.warn('⚠️ Bed size controls not found');
        }
        
        // Setup auth overlay click handler (if auth overlay exists)
        const authButton = document.getElementById('auth-overlay-login');
        if (authButton) {
            authButton.addEventListener('click', () => this.handleAuthClick());
            console.log('✅ Auth button click handler set up');
        } else {
            console.warn('⚠️ Auth overlay login button not found - auth overlay has been removed');
        }

        // Setup controls overlay click handler
        this.setupControlsClickHandler();

        // Setup subscription state observer
        this.setupSubscriptionObserver();
        
        // Create locked-plant modal (shown when non-premium user clicks a blurred plant)
        this.createLockedPlantModal();
        
        // Apply initial state immediately (before subscription notifications)
        console.log('🎯 Applying initial plant visibility state...');
        this.updatePlantVisibility();
    }

    createLockedPlantModal() {
        if (this.lockedPlantModal) return;
        const modal = document.createElement('div');
        modal.className = 'locked-plant-modal';
        modal.setAttribute('aria-hidden', 'true');
        modal.innerHTML = `
            <div class="locked-plant-overlay">
                <div class="locked-plant-content">
                    <button class="locked-plant-close" aria-label="Close modal">
                        <i class="fa fa-times"></i>
                    </button>
                    <div class="locked-plant-icon">
                        <i class="fa fa-leaf"></i>
                    </div>
                    <h2>Premium Plant Library</h2>
                    <p class="locked-plant-message">There are 150+ plants in the plant library. <br><br>The entire plant library is available with <b>Premium for $14.99/year.</b> <br>After checkout, sign in here with the <b>same email</b> to unlock access.</p>
                    <div class="locked-plant-buttons">
                        <a href="${getCheckoutUrl()}" target="_blank" rel="noopener noreferrer" class="auth-cta-btn" id="locked-plant-cta">
                            <i class="fa fa-star"></i>
                            Buy Now
                        </a>
                    </div>
                </div>
            </div>
        `;
        const style = document.createElement('style');
        style.textContent = `
            .locked-plant-modal { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.8); display: none; justify-content: center; align-items: center; z-index: 9999; backdrop-filter: blur(5px); }
            .locked-plant-modal.visible { display: flex; }
            .locked-plant-overlay { background: rgba(0,0,0,0.5); width: 100%; height: 100%; display: flex; justify-content: center; align-items: center; padding: 20px; }
            .locked-plant-content { background: white; border-radius: 16px; padding: 30px; max-width: 450px; width: 100%; text-align: center; position: relative; box-shadow: 0 12px 40px rgba(0,0,0,0.3); border: 2px solid rgba(76, 175, 80, 0.2); }
            .locked-plant-close { position: absolute; top: 15px; right: 15px; background: none; border: none; font-size: 20px; color: #666; cursor: pointer; padding: 8px; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
            .locked-plant-close:hover { background: #f0f0f0; color: #333; }
            .locked-plant-icon { font-size: 48px; color: #4CAF50; margin-bottom: 20px; }
            .locked-plant-content h2 { margin: 0 0 15px; font-size: 1.5rem; color: #333; }
            .locked-plant-message { margin: 0 0 24px; text-align: center; color: #555; font-size: 1rem; line-height: 1.5; }
            .locked-plant-buttons .auth-cta-btn { padding: 12px 24px; border-radius: 8px; border: none; cursor: pointer; font-weight: 600; font-size: 1rem; display: inline-flex; align-items: center; justify-content: center; gap: 8px; background: #FF9900; color: #fff; text-decoration: none; }
            .locked-plant-buttons .auth-cta-btn:hover { background: #43A047; color: #fff; }
        `;
        modal.appendChild(style);
        document.body.appendChild(modal);
        this.lockedPlantModal = modal;
        modal.querySelector('.locked-plant-close').addEventListener('click', () => this.hideLockedPlantModal());
        modal.querySelector('.locked-plant-overlay').addEventListener('click', (e) => { if (e.target.classList.contains('locked-plant-overlay')) this.hideLockedPlantModal(); });
        modal.querySelector('#locked-plant-cta').addEventListener('click', () => {
            trackCheckoutClick(PRODUCT_ANNUAL, 'locked_plant_modal');
            this.hideLockedPlantModal();
        });
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.lockedPlantModal && this.lockedPlantModal.classList.contains('visible')) {
                this.hideLockedPlantModal();
            }
        });
    }

    showLockedPlantModal() {
        if (!this.lockedPlantModal) this.createLockedPlantModal();
        // Refresh URL with current user so email + Firebase UID are always up-to-date
        const cta = this.lockedPlantModal.querySelector('#locked-plant-cta');
        if (cta) cta.href = getCheckoutUrl(PRODUCT_ANNUAL, subscriptionManager.currentUser);
        this.lockedPlantModal.classList.add('visible');
        this.lockedPlantModal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
    }

    hideLockedPlantModal() {
        if (this.lockedPlantModal) {
            this.lockedPlantModal.classList.remove('visible');
            this.lockedPlantModal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        }
    }

    setupSubscriptionObserver() {
        console.log('👂 Setting up subscription state observer...');
        
        // Listen for subscription changes with error handling
        subscriptionManager.onSubscriptionChange((subscriptionState) => {
            console.log('🔄 Plant auth subscription state change:', subscriptionState);
            
            // Wrap state updates in try-catch
            try {
                this.subscriptionState = subscriptionState;
                // Use requestAnimationFrame for UI updates to ensure DOM is ready
                requestAnimationFrame(() => {
                    this.updatePlantVisibility();
                });
            } catch (error) {
                console.error('❌ Error handling subscription state change:', error);
            }
        });
    }

    updatePlantVisibility() {
        if (!this.plantContainer || !this.plantSection) {
            console.error('❌ Missing required elements for plant visibility update');
            return;
        }

        const { isPremium } = this.subscriptionState;

        if (isPremium) {
            // Premium user - full access to all plants
            this.showPremiumAccess();
        } else {
            // Non-premium (logged-in or not): only first 10 vegetables accessible; rest blurred but visible
            this.showLimitedAccess();
        }
    }

    showLimitedAccess() {
        const { isLoggedIn } = this.subscriptionState;
        console.log('🔒 Showing limited access: first 10 vegetables only (non-premium)...');
        
        if (isLoggedIn) {
            this.removeMandatoryModal();
        }
        
        // Ensure vegetables filter is active (Herbs then Vegetables so scroll state is consistent)
        triggerVegetablesFilterWithScrollReset();
        
        // First 10 vegetables: unblurred and clickable. All others: blurred but visible.
        this.plantButtons.forEach(button => {
            button.style.display = 'flex';
            const plantName = button.textContent.trim();
            if (FIRST_10_VEGETABLES.includes(plantName)) {
                button.classList.remove('plant-blurred');
            } else {
                button.classList.add('plant-blurred');
            }
        });

        // Garden size controls (length, width, Change Garden Size, Reset Garden) are always usable for all users

        // Auth overlay message depends on login state
        if (this.authOverlay) {
            if (isLoggedIn) {
                this.updateAuthOverlay('Upgrade to Premium to unlock the full plant library', 'Upgrade to Premium');
            } else {
                this.updateAuthOverlay('Sign in or upgrade to Premium to unlock more plants', 'Sign In');
            }
            this.authOverlay.classList.add('visible');
            this.authOverlay.classList.remove('complete-block');
        }
        this.plantContainer.classList.add('user-logged-in');
        this.plantSection.classList.remove('blocked');
        
        console.log('✅ Limited access applied - first 10 vegetables active, rest blurred');
    }

    showSignInModal() {
        console.log('🔑 Triggering sign-in modal...');
        
        // Find the auth modal trigger
        const showAuthModal = document.getElementById('show-auth-modal');
        if (showAuthModal) {
            // Directly trigger the modal without setTimeout
            showAuthModal.click();
            console.log('✅ Sign-in modal opened');
            this.makeMandatoryModal();
        } else {
            console.error('❌ Auth modal trigger not found');
            // Fallback: show the overlay if modal trigger is not found
            this.updateAuthOverlay('Sign in to unlock full plant library', 'Sign In');
            this.authOverlay.classList.add('visible', 'complete-block');
        }
    }

    makeMandatoryModal() {
        console.log('🔒 Making auth modal mandatory...');
        
        // Find the auth modal container
        const authContainer = document.querySelector('.auth-container');
        if (authContainer) {
            // Add a flag to track that this is mandatory
            authContainer.setAttribute('data-mandatory', 'true');
            
            // Add event listener to prevent closing if not already added
            if (!authContainer.dataset.hasModalListener) {
                authContainer.dataset.hasModalListener = 'true';
                authContainer.addEventListener('click', (e) => {
                    if (e.target === authContainer && authContainer.getAttribute('data-mandatory') === 'true') {
                        e.preventDefault();
                        e.stopPropagation();
                        return false;
                    }
                });
            }
            
            console.log('✅ Auth modal is now mandatory');
        } else {
            console.warn('⚠️ Auth container not found when trying to make mandatory');
        }
    }

    removeMandatoryModal() {
        console.log('🔓 Removing mandatory auth modal restrictions...');
        
        const authContainer = document.querySelector('.auth-container');
        if (authContainer) {
            // Remove mandatory flag
            authContainer.removeAttribute('data-mandatory');
            
            console.log('✅ Auth modal restrictions removed');
        } else {
            console.warn('⚠️ Auth container not found when trying to remove mandatory restrictions');
        }
    }

    showPremiumAccess() {
        console.log('⭐ Showing premium access for premium user...');
        
        // Remove mandatory modal restrictions since user is logged in
        this.removeMandatoryModal();
        
        // Show all plant buttons
        this.plantButtons.forEach(button => {
            button.style.display = 'flex';
            button.classList.remove('plant-blurred');
        });

        // Remove blur from bed size controls
        if (this.bedSizeControls) {
            this.bedSizeControls.classList.remove('controls-blurred');
        }

        // Hide auth overlay completely (if it exists)
        if (this.authOverlay) {
            this.authOverlay.classList.remove('visible', 'complete-block');
        }
        this.plantContainer.classList.add('user-logged-in');
        this.plantSection.classList.remove('blocked');
        
        // Trigger vegetables filter (Herbs then Vegetables so scroll state is consistent)
        triggerVegetablesFilterWithScrollReset();
        console.log('✅ Vegetables filter activated for premium user');
        
        console.log('✅ Premium access applied - all plants and controls visible');
    }

    updateAuthOverlay(message, buttonText) {
        // Only update if auth overlay exists
        if (!this.authOverlay) return;
        
        // Update overlay message
        const overlayMessage = this.authOverlay.querySelector('.auth-overlay-message');
        if (overlayMessage) {
            overlayMessage.textContent = message;
        }

        // Update button text
        const authButton = document.getElementById('auth-overlay-login');
        if (authButton) {
            authButton.textContent = buttonText;
        }
    }

    setupControlsClickHandler() {
        if (this.bedSizeControls) {
            this.bedSizeControls.addEventListener('click', (e) => {
                // Only trigger auth/upgrade if controls are blurred
                if (this.bedSizeControls.classList.contains('controls-blurred')) {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('🔒 Controls clicked while blurred - triggering auth/upgrade');
                    this.handleAuthClick();
                }
            });
        }
    }

    handleAuthClick() {
        const { isLoggedIn, isPremium } = this.subscriptionState;
        
        if (!isLoggedIn) {
            // User not logged in - show auth modal
            console.log('🔑 Auth click triggered - opening auth modal');
            const showAuthModal = document.getElementById('show-auth-modal');
            if (showAuthModal && !showAuthModal.dataset.processing) {
                showAuthModal.dataset.processing = 'true';
                showAuthModal.click();
                console.log('✅ Auth modal triggered');
                setTimeout(() => {
                    delete showAuthModal.dataset.processing;
                }, 500);
            } else {
                console.error('❌ Auth modal trigger not found or processing');
            }
        } else if (!isPremium) {
            // User logged in but not premium - trigger upgrade
            console.log('💳 Upgrade click triggered - processing upgrade...');
            this.handleUpgradeClick();
        }
    }

    handleUpgradeClick() {
        trackCheckoutClick(PRODUCT_ANNUAL, 'upgrade_button');
        console.log('💳 Opening Lemon Squeezy checkout page in new tab...');
        const newTab = window.open(getCheckoutUrl(PRODUCT_ANNUAL, subscriptionManager.currentUser), '_blank');
        if (newTab) {
            newTab.focus();
        }
    }




    // Legacy method for backward compatibility
    onAuthStateChange(user) {
        console.log('🔄 Legacy auth state change call - now handled by subscription manager');
        // This is now handled by the subscription manager
    }

    // Get current access info
    getAccessInfo() {
        return {
            subscriptionState: this.subscriptionState,
            visiblePlants: this.subscriptionState.accessiblePlantCount,
            totalPlants: this.plantButtons.length,
            canAccessControls: this.subscriptionState.canAccessBedSizeControls
        };
    }
}

// Initialize and export the plant auth manager
export const initializePlantAuth = () => {
    console.log('🚀 Initializing plant authentication system...');
    const plantAuthManager = new PlantAuthManager();
    
    // Make it available globally for backward compatibility
    window.PlantAuthManager = PlantAuthManager;
    window.plantAuthManager = plantAuthManager;
    
    // Add a manual test function for debugging
    window.testPlantAuth = () => {
        console.log('🧪 Testing plant auth system...');
        console.log('Plant container:', plantAuthManager.plantContainer);
        console.log('Plant section:', plantAuthManager.plantSection);
        console.log('Auth overlay:', plantAuthManager.authOverlay);
        console.log('Plant buttons count:', plantAuthManager.plantButtons.length);
        console.log('Bed size controls:', plantAuthManager.bedSizeControls);
        console.log('Current subscription state:', plantAuthManager.subscriptionState);
        console.log('Access info:', plantAuthManager.getAccessInfo());
    };
    
    console.log('✅ Plant authentication system initialized');
    console.log('💡 Run window.testPlantAuth() in console to test the system');
    return plantAuthManager;
}; 